export function parseCustomExclusions(text) {
  return String(text || '')
    .split(/[,;]+/)
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);
}

/** Airtable "Allergies Diet" record IDs the member has flagged, via the quiz
 *  or the manual RestrictionSearch picker — both write into the same field. */
export function getRestrictionTagIds(member) {
  if (!member) return [];
  return [
    ...(member.restrictionTags || []),
    ...(member.profile?.restrictions || []),
  ].filter((t, i, arr) => t && t !== 'none' && arr.indexOf(t) === i);
}

export function restrictionLabel(id, member) {
  const labelMap = member?.restrictionLabels || member?.profile?.restrictionLabels || {};
  return labelMap[id] || id;
}

export function restrictionSummary(member) {
  const labelMap = member?.restrictionLabels || member?.profile?.restrictionLabels || {};
  const tagIds = getRestrictionTagIds(member);
  const parts = tagIds.map((id) => labelMap[id] || id);
  const custom = parseCustomExclusions(member?.customExclusionsText);
  return [...parts, ...custom].length ? [...parts, ...custom].join(', ') : 'No restrictions set';
}

/** Does this ingredient conflict with the member's allergies/diet?
 *  Tag-based restrictions (quiz + RestrictionSearch picker) match on the
 *  ingredient's real Airtable "Allergen" link — the same Allergies Diet
 *  record IDs the picker writes — mirroring the backend's
 *  getClientAllergiesIngredientIds/markOriginalIngredientsActive used on the
 *  meal-plan endpoint. Free-text custom exclusions have no linked record, so
 *  those fall back to a name substring match. */
export function ingredientConflictsWithMember(ingredient, member) {
  if (!member || !ingredient) return false;

  const tagIds = getRestrictionTagIds(member);
  if (tagIds.length) {
    const allergenIds = ingredient.allergen || [];
    if (tagIds.some((id) => allergenIds.includes(id))) return true;
  }

  const customTerms = parseCustomExclusions(member.customExclusionsText);
  if (customTerms.length) {
    const name = String(ingredient.name || '').toLowerCase();
    if (customTerms.some((term) => name.includes(term))) return true;
  }

  return false;
}

/** Which restrictions this ingredient trips, named as the member chose them —
 *  "Tree Nuts" rather than "SF Almonds". A free-text exclusion has no linked
 *  record, so the typed term is its label. Mirrors the matching in
 *  ingredientConflictsWithMember: anything that flags, this explains. */
export function memberConflictLabels(ingredient, member) {
  if (!member || !ingredient) return [];
  const labels = [];

  const allergenIds = ingredient.allergen || [];
  getRestrictionTagIds(member).forEach((id) => {
    if (allergenIds.includes(id)) labels.push(restrictionLabel(id, member));
  });

  const name = String(ingredient.name || '').toLowerCase();
  parseCustomExclusions(member.customExclusionsText).forEach((term) => {
    if (name.includes(term)) labels.push(term);
  });

  return [...new Set(labels)].filter(Boolean);
}

/** The ingredients that clash with `member`, in the same shape
 *  computeAutoSubstitution reports in `allergenConflicts` — so the same badge,
 *  banner and label helpers read it. Used when a meal is reassigned to someone
 *  else: the ingredients are already chosen, so they're judged as they are
 *  rather than re-substituted. */
export function memberIngredientConflicts(ingredients, member) {
  return (ingredients || [])
    .filter((ingredient) => ingredientConflictsWithMember(ingredient, member))
    .map((ingredient) => ({
      id: ingredient.id,
      name: ingredient.name,
      labels: memberConflictLabels(ingredient, member),
    }));
}

export function scoreMenuForMember(dishes, member) {
  const goal = member?.profile?.goal;

  return dishes
    .map((dish) => {
      let score = 0;
      const name = (dish.name || '').toLowerCase();

      if ((goal === 'muscle-gain' || goal === 'gain-muscle') && /chicken|salmon|turkey|protein/.test(name)) score += 2;
      if ((goal === 'weight-loss' || goal === 'lose-weight') && /vegetable|tofu|salad|roasted/.test(name)) score += 2;
      if (member?.hasFullQuiz) score += 1;

      return { ...dish, recommended: score >= 2, score };
    })
    .sort((a, b) => (b.score || 0) - (a.score || 0));
}
